import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    
    // Update canonical
    if (canonical) {
      updateLinkTag('canonical', `${import.meta.env.VITE_SITE_URL || ''}${canonical}`);
    }

    // Update OG tags
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    if (canonical) {
      updateMetaTag('og:url', `${import.meta.env.VITE_SITE_URL || ''}${canonical}`, 'property');
    }
    if (ogImage) {
      updateMetaTag('og:image', ogImage, 'property');
    }

    // Update Twitter tags
    updateMetaTag('twitter:title', title, 'property');
    updateMetaTag('twitter:description', description, 'property');
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage, 'property');
    }

    // Update last-modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Add Schema.org JSON-LD
    if (schema) {
      addSchemaScript(schema);
    }

    return () => {
      // Cleanup schema script on unmount
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogImage, schema]);
}

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function addSchemaScript(schema: object) {
  // Remove existing schema script
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new schema script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
}
